/**
 * @file care-item-response.schema.ts
 * @description Resposta de uma usuária a um item de cuidado, e o estado
 * consolidado de cuidado por usuária.
 *
 * `careItemResponses` é a trilha de auditoria clínica: guarda a versão exata da
 * definição respondida, as respostas cruas, o snapshot das variáveis no momento
 * da avaliação e a lista de ações disparadas com o porquê. Requisito do épico —
 * responsabilidade clínica e legal exige poder reconstruir por que o app disse
 * o que disse.
 *
 * `userCareStates` é o documento de leitura rápida do motor e da home: um por
 * usuária, com as variáveis de escopo USER e os itens já concluídos. Toda
 * escrita passa pelo motor (nunca `updateByPath` — é por aí que entra documento
 * que quebra a listagem depois).
 */

import { z } from 'zod';
import { CareActionSchema, CareAlertSeverityEnum } from './care-action.schema';

/** Valor de resposta de um bloco. `null` = pulado/desconhecido. */
export const CareAnswerValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.date(),
  z.null(),
]);
export type CareAnswerValue = z.infer<typeof CareAnswerValueSchema>;

/**
 * Ação efetivamente disparada, com a origem. `sourceRuleId` aponta para a regra
 * de item; `sourceBlockId`/`sourceOptionId` para a regra de bloco.
 */
export const FiredCareActionSchema = z.object({
  action: CareActionSchema,
  sourceRuleId: z.string().nullable().optional(),
  sourceBlockId: z.string().nullable().optional(),
  sourceOptionId: z.string().nullable().optional(),
});
export type FiredCareAction = z.infer<typeof FiredCareActionSchema>;

/**
 * Recomendação acumulada durante o fluxo e exibida no fechamento. Persistida
 * para alimentar a área "recomendações para você" — a spec é explícita: as
 * recomendações ficam acessíveis depois, mesmo após a usuária sair.
 */
export const CareRecommendationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('CONTENT'),
    courseId: z.string(),
    moduleIndex: z.number().int().min(0).nullable().optional(),
    classIndex: z.number().int().min(0).nullable().optional(),
    label: z.string().nullable().optional(),
    /**
     * Título do conteúdo, gravado pela API junto com o `label` quando a ação
     * aponta uma aula ou módulo: o card mostra a aula e, embaixo, de onde ela
     * é. Recomendação gravada antes disso não tem nenhum dos dois.
     */
    courseTitle: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('TOOL'),
    toolKey: z.string(),
    label: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('MESSAGE'),
    title: z.string().nullable().optional(),
    body: z.string(),
    severity: CareAlertSeverityEnum.default('INFO'),
  }),
]);
export type CareRecommendation = z.infer<typeof CareRecommendationSchema>;

export const CareItemResponseSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  /** Versão da definição respondida — congela o significado das respostas. */
  itemVersion: z.number().int().positive(),
  userId: z.string(),
  pregnancyId: z.string().nullable().optional(),
  /**
   * Marca da sessão em que o registro aconteceu (A052, fase 2). Opcional
   * enquanto o dado antigo é backfillado; a leitura passa a filtrar por ele
   * na fase 3. Cada CRM vê só o que aconteceu na própria marca.
   */
  brandId: z.string().nullable().optional(),

  /** Respostas cruas, indexadas por id de bloco. */
  answers: z.record(CareAnswerValueSchema).default({}),

  /** Snapshot das variáveis (nativas incluídas) no instante da avaliação. */
  computedVariables: z.record(z.any()).default({}),

  /** Escores calculados, indexados pelo nome da variável. Ex.: `{ phq9_total: 14 }`. */
  scores: z.record(z.number()).default({}),

  firedActions: z.array(FiredCareActionSchema).default([]),
  recommendations: z.array(CareRecommendationSchema).default([]),

  /** Rodada: refazer o item cria uma resposta nova com índice incrementado. */
  roundIndex: z.number().int().min(0).default(0),

  /**
   * Chave de idempotência enviada pelo app. Mesmo padrão de `appointments`:
   * retry de rede ou duplo toque não pode virar duas rodadas — e em dado
   * clínico, rodada fantasma vira histórico falso de evolução.
   */
  idempotencyKey: z.string().nullable().optional(),

  completedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});

export type CareItemResponse = z.infer<typeof CareItemResponseSchema>;

// ---------------------------------------------------------------------------
// Estado consolidado
// ---------------------------------------------------------------------------

export const StoredCareVariableSchema = z.object({
  value: z.any(),
  /** Item que gravou o valor — para o admin explicar de onde veio. */
  sourceItemId: z.string().nullable().optional(),
  updatedAt: z.date(),
});

export const CompletedCareItemSchema = z.object({
  itemId: z.string(),
  lastResponseId: z.string(),
  lastVersion: z.number().int().positive(),
  roundCount: z.number().int().positive().default(1),
  completedAt: z.date(),
});

/** Ferramenta ativada por uma recomendação/ativação por contexto. */
export const ActiveCareToolSchema = z.object({
  toolKey: z.string(),
  activatedAt: z.date(),
  sourceItemId: z.string().nullable().optional(),
});

export const UserCareStateSchema = z.object({
  /**
   * `id` == `userId`: um documento por usuária — até a A052. Com o escopo por
   * vínculo (fase 3), o estado é por (usuária, marca): `id` == `userId:brandId`
   * e `brandId` preenchido. O documento legado (`id` == `userId`) continua
   * válido e é copiado para a marca da própria usuária na primeira leitura.
   */
  id: z.string(),
  userId: z.string(),
  brandId: z.string().nullable().optional(),
  variables: z.record(StoredCareVariableSchema).default({}),
  completedItems: z.array(CompletedCareItemSchema).default([]),
  /** Itens abertos por `ACTIVATE_ITEM` que ainda não foram respondidos. */
  activatedItemIds: z.array(z.string()).default([]),
  activeTools: z.array(ActiveCareToolSchema).default([]),
  /**
   * Recomendações vivas, agregadas da última resposta de cada item.
   *
   * Carregam `sourceItemId` para o motor saber quais substituir quando a
   * usuária refaz um checkpoint. Sem a origem, uma recomendação que a nova
   * rodada deixou de fazer sobreviveria para sempre.
   */
  savedRecommendations: z
    .array(CareRecommendationSchema.and(z.object({ sourceItemId: z.string().optional() })))
    .default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});

export type UserCareState = z.infer<typeof UserCareStateSchema>;
export type StoredCareVariable = z.infer<typeof StoredCareVariableSchema>;
export type CompletedCareItem = z.infer<typeof CompletedCareItemSchema>;
export type ActiveCareTool = z.infer<typeof ActiveCareToolSchema>;
